// portfolioTypes.ts
// Shared types, constants, helpers and mock data used by PortfolioList, PortfolioDetail and PortfolioConnector

import type { AddedRule } from './AddPortfolioRulesModal';
import type { CreditSafePortfolio, CreditsafePortfolioRule } from '@dill/dill-shared';

export type { AddedRule, CreditSafePortfolio, CreditsafePortfolioRule };

// ─────────────────────────────────────────────
// CATEGORY / COLOR SYSTEM
// ─────────────────────────────────────────────

export const CATEGORIES = {
  credit_score: { label: 'Credit Score', color: 'blue', icon: '📊' },
  credit_limit: { label: 'Credit Limit', color: 'indigo', icon: '💳' },
  company_info: { label: 'Company Info', color: 'slate', icon: '🏢' },
  financials: { label: 'Financials', color: 'green', icon: '📑' },
  payment: { label: 'Payment Behaviour', color: 'cyan', icon: '💰' },
  risk: { label: 'Risk Events', color: 'orange', icon: '⚠️' },
  bankruptcy: { label: 'Bankruptcy & Legal', color: 'red', icon: '⚖️' },
} as const;

export type CategoryKey = keyof typeof CATEGORIES;

export const COLOR_MAP = {
  blue: {
    badge: 'bg-blue-50 text-blue-700 border-blue-200',
    dot: 'bg-blue-500',
    strip: 'bg-blue-500',
    pill: 'bg-blue-100 text-blue-700',
    bar: 'bg-blue-500',
  },
  indigo: {
    badge: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    dot: 'bg-indigo-500',
    strip: 'bg-indigo-500',
    pill: 'bg-indigo-100 text-indigo-700',
    bar: 'bg-indigo-500',
  },
  slate: {
    badge: 'bg-slate-100 text-slate-600 border-slate-200',
    dot: 'bg-slate-400',
    strip: 'bg-slate-400',
    pill: 'bg-slate-100 text-slate-600',
    bar: 'bg-slate-400',
  },
  green: {
    badge: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    dot: 'bg-emerald-500',
    strip: 'bg-emerald-500',
    pill: 'bg-emerald-100 text-emerald-700',
    bar: 'bg-emerald-500',
  },
  cyan: {
    badge: 'bg-cyan-50 text-cyan-700 border-cyan-200',
    dot: 'bg-cyan-500',
    strip: 'bg-cyan-500',
    pill: 'bg-cyan-100 text-cyan-700',
    bar: 'bg-cyan-500',
  },
  orange: {
    badge: 'bg-orange-50 text-orange-700 border-orange-200',
    dot: 'bg-orange-500',
    strip: 'bg-orange-500',
    pill: 'bg-orange-100 text-orange-700',
    bar: 'bg-orange-500',
  },
  red: {
    badge: 'bg-red-50 text-red-700 border-red-200',
    dot: 'bg-red-500',
    strip: 'bg-red-500',
    pill: 'bg-red-100 text-red-700',
    bar: 'bg-red-500',
  },
  amber: {
    badge: 'bg-amber-50 text-amber-700 border-amber-200',
    dot: 'bg-amber-500',
    strip: 'bg-amber-500',
    pill: 'bg-amber-100 text-amber-700',
    bar: 'bg-amber-500',
  },
} as const;

export type ColorType = keyof typeof COLOR_MAP;

export const CAT_ORDER: CategoryKey[] = [
  'credit_score',
  'credit_limit',
  'payment',
  'company_info',
  'financials',
  'risk',
  'bankruptcy',
];

export const FREQUENCY_OPTIONS = ['Daily', 'Weekly', 'Monthly', 'Immediate'] as const;
export type FrequencyOption = (typeof FREQUENCY_OPTIONS)[number];

export const FREQUENCY_COLORS: Record<string, string> = {
  Immediate: 'bg-red-50 text-red-600 border-red-200',
  Daily: 'bg-blue-50 text-blue-600 border-blue-200',
  Weekly: 'bg-purple-50 text-purple-600 border-purple-200',
  Monthly: 'bg-slate-100 text-slate-600 border-slate-200',
};

export const LANGUAGE_OPTIONS = [
  { value: 'en', label: 'English' },
  { value: 'de', label: 'German' },
  { value: 'fr', label: 'French' },
  { value: 'es', label: 'Spanish' },
  { value: 'nl', label: 'Dutch' },
  { value: 'no', label: 'Norwegian' },
  { value: 'fi', label: 'Finnish' },
  { value: 'sv', label: 'Swedish' },
];

export type EmailRecipient = { firstName: string; lastName: string; emailAddress: string };

export type PortfolioEditState = {
  name: string;
  frequency: FrequencyOption;
  emailSubject: string;
  emailLanguage: string;
  isDefault: boolean;
  emails: EmailRecipient[];
};

// ─────────────────────────────────────────────
// RULE HELPERS
// ─────────────────────────────────────────────

export function getRuleCategory(ruleCode: number): CategoryKey {
  const map: [number[], CategoryKey][] = [
    [[101, 110], 'credit_score'],
    [[102], 'credit_limit'],
    [[103, 104, 107, 109, 1602, 1603, 1612], 'company_info'],
    [[105, 106, 1605], 'risk'],
    [[108], 'financials'],
    [[1601], 'credit_score'],
    [[1604], 'payment'],
    [[1606, 1607, 1611], 'risk'],
    [[1608, 1609, 1610], 'bankruptcy'],
    [[111], 'bankruptcy'],
  ];
  for (const [codes, cat] of map) {
    if (codes.includes(ruleCode)) return cat;
  }
  if (ruleCode >= 1600 && ruleCode < 1700) return 'risk';
  return 'company_info';
}

export function getRuleName(ruleCode: number, apiName: string): string {
  const names: Record<number, string> = {
    101: 'International Score Band Change',
    102: 'Credit Limit Change',
    103: 'Company Name Change',
    104: 'Registered Address Change',
    105: 'Company Status Change',
    106: 'Court Judgment Filed',
    107: 'Director / Officer Change',
    108: 'Annual Accounts Filed',
    109: 'Industry Code Change',
    110: 'Credit Score % Change',
    111: 'Insolvency / Administration Filing',
    1601: 'Credit Rating Change',
    1602: 'Company Name Change',
    1603: 'Company Address Change',
    1604: 'DBT Change',
    1605: 'Company Status Change',
    1606: 'Adverse Filing Count Changed',
    1607: 'Adverse Filing Amount Changed',
    1608: 'Bankruptcy Filed',
    1609: 'Court Judgment Filed',
    1610: 'Tax Lien Filed',
    1611: 'UCC Filing Changed',
    1612: 'Industry Code Change',
  };
  return names[ruleCode] ?? apiName;
}

export function getPortfolioHealth(
  portfolio: CreditSafePortfolio
): 'healthy' | 'warning' | 'empty' {
  const rules = portfolio.rules ?? [];
  const active = rules.filter((r) => r.isActive === 1);
  if (active.length === 0) return 'empty';
  const noEmails = portfolio.emails.length === 0;
  if (noEmails) return 'warning';
  return 'healthy';
}

// ─────────────────────────────────────────────
// MOCK DATA
// ─────────────────────────────────────────────

export const MOCK_PORTFOLIOS: CreditSafePortfolio[] = [
  {
    id: 'a1b2c3d4-e5f6-7890-abcd-ef1234567890',
    correlationId: 'corr-001',
    portfolioId: 1001,
    name: 'Key Suppliers',
    isDefault: true,
    emails: [
      { firstName: 'Sarah', lastName: 'Mitchell', emailAddress: 'sarah.mitchell@acme.com' },
      { firstName: 'Tom', lastName: 'Brooks', emailAddress: 'tom.brooks@acme.com' },
    ],
    emailSubject: 'Supplier Credit Alert',
    emailLanguage: 'en',
    frequency: 'Daily',
    supplierId: 'sup-001',
    rules: [
      {
        isActive: 1,
        ruleCode: 101,
        ruleCountryCode: 'XX',
        ruleType: 4,
        ruleTypeName: 'Rating band change',
        name: 'International Score Band Change',
        param0: '1',
        param1: '',
        param2: 'true',
      },
      {
        isActive: 1,
        ruleCode: 102,
        ruleCountryCode: 'XX',
        ruleType: 2,
        ruleTypeName: 'Credit limit change',
        name: 'Credit Limit Change',
        param0: 'false',
        param1: '20',
        param2: '5000',
      },
      {
        isActive: 1,
        ruleCode: 105,
        ruleCountryCode: 'XX',
        ruleType: 1,
        ruleTypeName: 'Status change',
        name: 'Company Status Change',
      },
      {
        isActive: 1,
        ruleCode: 111,
        ruleCountryCode: 'XX',
        ruleType: 1,
        ruleTypeName: 'Insolvency',
        name: 'Insolvency / Administration Filing',
      },
      {
        isActive: 1,
        ruleCode: 106,
        ruleCountryCode: 'XX',
        ruleType: 1,
        ruleTypeName: 'Legal',
        name: 'Court Judgment Filed',
      },
      {
        isActive: 0,
        ruleCode: 107,
        ruleCountryCode: 'XX',
        ruleType: 1,
        ruleTypeName: 'Director change',
        name: 'Director / Officer Change',
      },
    ],
    createdAt: new Date('2024-01-15'),
    updatedAt: new Date('2024-11-02'),
  },
  {
    id: 'b2c3d4e5-f6a7-8901-bcde-f12345678901',
    correlationId: 'corr-002',
    portfolioId: 1002,
    name: 'US Customers',
    isDefault: false,
    emails: [{ firstName: 'James', lastName: 'Carter', emailAddress: 'james.carter@acme.com' }],
    emailSubject: 'US Customer Risk Update',
    emailLanguage: 'en',
    frequency: 'Immediate',
    supplierId: 'sup-001',
    rules: [
      {
        isActive: 1,
        ruleCode: 1601,
        ruleCountryCode: 'US',
        ruleType: 2,
        ruleTypeName: 'Rating change',
        name: 'Credit Rating Change',
        param0: '10',
        param1: '20',
      },
      {
        isActive: 1,
        ruleCode: 1604,
        ruleCountryCode: 'US',
        ruleType: 2,
        ruleTypeName: 'DBT change',
        name: 'DBT Change',
        param0: '15',
        param1: '5',
      },
      {
        isActive: 1,
        ruleCode: 1608,
        ruleCountryCode: 'US',
        ruleType: 1,
        ruleTypeName: 'Bankruptcy',
        name: 'Bankruptcy Filed',
      },
      {
        isActive: 1,
        ruleCode: 1609,
        ruleCountryCode: 'US',
        ruleType: 1,
        ruleTypeName: 'Judgment',
        name: 'Court Judgment Filed',
      },
      {
        isActive: 1,
        ruleCode: 1610,
        ruleCountryCode: 'US',
        ruleType: 1,
        ruleTypeName: 'Tax lien',
        name: 'Tax Lien Filed',
      },
      {
        isActive: 1,
        ruleCode: 1606,
        ruleCountryCode: 'US',
        ruleType: 2,
        ruleTypeName: 'Derogatory count',
        name: 'Adverse Filing Count Changed',
        param0: '10',
        param1: '3',
      },
      {
        isActive: 0,
        ruleCode: 1602,
        ruleCountryCode: 'US',
        ruleType: 1,
        ruleTypeName: 'Name change',
        name: 'Company Name Change',
      },
    ],
    createdAt: new Date('2024-03-20'),
    updatedAt: new Date('2024-12-10'),
  },
  {
    id: 'c3d4e5f6-a7b8-9012-cdef-123456789012',
    correlationId: 'corr-003',
    portfolioId: 1003,
    name: 'Strategic Partners',
    isDefault: false,
    emails: [
      { firstName: 'Lisa', lastName: 'Wang', emailAddress: 'lisa.wang@acme.com' },
      { firstName: 'David', lastName: 'Osei', emailAddress: 'd.osei@acme.com' },
      { firstName: 'Priya', lastName: 'Patel', emailAddress: 'priya.patel@acme.com' },
    ],
    emailSubject: 'Partner Monitoring Alert',
    emailLanguage: 'en',
    frequency: 'Weekly',
    supplierId: 'sup-001',
    rules: [
      {
        isActive: 1,
        ruleCode: 101,
        ruleCountryCode: 'XX',
        ruleType: 4,
        ruleTypeName: 'Rating band change',
        name: 'International Score Band Change',
        param0: '2',
        param1: 'Any',
        param2: 'true',
      },
      {
        isActive: 1,
        ruleCode: 107,
        ruleCountryCode: 'XX',
        ruleType: 1,
        ruleTypeName: 'Director change',
        name: 'Director / Officer Change',
      },
      {
        isActive: 1,
        ruleCode: 108,
        ruleCountryCode: 'XX',
        ruleType: 1,
        ruleTypeName: 'Financials',
        name: 'Annual Accounts Filed',
      },
      {
        isActive: 0,
        ruleCode: 105,
        ruleCountryCode: 'XX',
        ruleType: 1,
        ruleTypeName: 'Status',
        name: 'Company Status Change',
      },
    ],
    createdAt: new Date('2024-06-01'),
    updatedAt: new Date('2024-10-15'),
  },
  {
    id: 'd4e5f6a7-b8c9-0123-def0-234567890123',
    correlationId: 'corr-004',
    portfolioId: 1004,
    name: 'New Prospects',
    isDefault: false,
    emails: [],
    emailSubject: '',
    emailLanguage: 'en',
    frequency: 'Monthly',
    supplierId: 'sup-001',
    rules: [],
    createdAt: new Date('2024-09-12'),
  },
  {
    id: 'e5f6a7b8-c9d0-1234-ef01-345678901234',
    correlationId: 'corr-005',
    portfolioId: 1005,
    name: 'European Vendors',
    isDefault: false,
    emails: [{ firstName: 'Marie', lastName: 'Dupont', emailAddress: 'marie.dupont@acme.com' }],
    emailSubject: 'EU Vendor Alert',
    emailLanguage: 'fr',
    frequency: 'Weekly',
    supplierId: 'sup-001',
    rules: [
      {
        isActive: 1,
        ruleCode: 101,
        ruleCountryCode: 'XX',
        ruleType: 4,
        ruleTypeName: 'Rating band change',
        name: 'International Score Band Change',
        param0: '1',
        param1: 'C',
        param2: 'true',
      },
      {
        isActive: 1,
        ruleCode: 105,
        ruleCountryCode: 'XX',
        ruleType: 1,
        ruleTypeName: 'Status change',
        name: 'Company Status Change',
      },
      {
        isActive: 1,
        ruleCode: 108,
        ruleCountryCode: 'XX',
        ruleType: 1,
        ruleTypeName: 'Financials',
        name: 'Annual Accounts Filed',
      },
    ],
    createdAt: new Date('2024-05-10'),
    updatedAt: new Date('2024-09-20'),
  },
  {
    id: 'f6a7b8c9-d0e1-2345-f012-456789012345',
    correlationId: 'corr-006',
    portfolioId: 1006,
    name: 'Watchlist',
    isDefault: false,
    emails: [],
    emailSubject: 'Watchlist Alert',
    emailLanguage: 'en',
    frequency: 'Immediate',
    supplierId: 'sup-001',
    rules: [
      {
        isActive: 1,
        ruleCode: 111,
        ruleCountryCode: 'XX',
        ruleType: 1,
        ruleTypeName: 'Insolvency',
        name: 'Insolvency / Administration Filing',
      },
      {
        isActive: 1,
        ruleCode: 106,
        ruleCountryCode: 'XX',
        ruleType: 1,
        ruleTypeName: 'Legal',
        name: 'Court Judgment Filed',
      },
    ],
    createdAt: new Date('2024-08-01'),
  },
];
